import { useCallback, useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';
import { company } from '../../site/company';
import { programSlides } from '../../site/program-slides';
import { ProgramLightbox } from './program-lightbox';

const AUTOPLAY_MS = 25000;
const SWIPE_PX = 40;

/**
 * The mortgage-programme posters: one wide slide at a time, with dots, arrows, an autoplay bar and
 * a fullscreen viewer behind the magnifier.
 *
 * **Twenty-five seconds a slide is deliberate and long.** These are dense posters full of rates and
 * conditions, not a decorative reel — a slide that changes while somebody is still reading it is
 * worse than no rotation at all.
 *
 * The progress bar is driven by writing a transition to the element rather than by a React state
 * that ticks: the bar runs for the whole twenty-five seconds and re-rendering the section forty
 * times a second to move it would be the most expensive thing on the page.
 */
export function ProgramsSection() {
    const { t } = useTranslation();
    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);
    const [openedAt, setOpenedAt] = useState<number | null>(null);
    const progress = useRef<HTMLDivElement>(null);
    const touchStartX = useRef(0);

    const restartProgress = useCallback((running: boolean) => {
        const bar = progress.current;

        if (bar === null) {
            return;
        }

        bar.style.transition = 'none';
        bar.style.width = '0%';

        if (!running) {
            return;
        }

        // Reading the layout forces the reset to take effect before the transition is attached;
        // without it the browser coalesces both writes and the bar never moves.
        void bar.offsetWidth;
        bar.style.transition = `width ${String(AUTOPLAY_MS)}ms linear`;
        bar.style.width = '100%';
    }, []);

    const go = useCallback(
        (next: number) => setCurrent((next + programSlides.length) % programSlides.length),
        []
    );

    useEffect(() => {
        const running = !paused && openedAt === null;

        restartProgress(running);

        if (!running) {
            return;
        }

        const timer = window.setTimeout(() => go(current + 1), AUTOPLAY_MS);

        return () => window.clearTimeout(timer);
    }, [current, paused, openedAt, go, restartProgress]);

    const onTouchEnd = (event: ReactTouchEvent<HTMLDivElement>) => {
        const dx = (event.changedTouches[0]?.clientX ?? 0) - touchStartX.current;

        if (Math.abs(dx) > SWIPE_PX) {
            go(current + (dx < 0 ? 1 : -1));
        }
    };

    return (
        <section className="imgslider-section" id="mortgage-programs">
            <div className="container">
                <div className="section-header centered" style={{ marginBottom: 40 }}>
                    <span className="section-label">{t('site.prog.label')}</span>
                    <h2 className="section-title">{t('site.prog.title')}</h2>
                    <p className="section-subtitle">{t('site.prog.subtitle')}</p>
                </div>

                <div
                    className="imgslider-wrapper"
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                >
                    {/* The viewport clips the sliding track. The controls sit outside it so they can
                        overlay the poster on a desktop and drop below it on a telephone. */}
                    <div className="imgslider-viewport">
                        <div className="imgslider-progress" ref={progress} />

                        <div
                            className="imgslider-track"
                            style={{ transform: `translateX(-${String(current * 100)}%)` }}
                            onTouchStart={(event) => {
                                touchStartX.current = event.touches[0]?.clientX ?? 0;
                            }}
                            onTouchEnd={onTouchEnd}
                        >
                            {programSlides.map((slide, position) => (
                                <div
                                    key={slide}
                                    className={
                                        position === current
                                            ? 'imgslider-slide imgslider-active'
                                            : 'imgslider-slide'
                                    }
                                >
                                    <img
                                        src={slide}
                                        alt={`${t('site.prog.alt')} ${String(position + 1)}`}
                                        loading="lazy"
                                        onClick={() => setOpenedAt(position)}
                                    />
                                </div>
                            ))}
                        </div>

                        <button
                            className="imgslider-zoom"
                            type="button"
                            aria-label={t('site.prog.zoom')}
                            onClick={() => setOpenedAt(current)}
                        >
                            <svg viewBox="0 0 24 24">
                                <circle cx="11" cy="11" r="7" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                <line x1="11" y1="8" x2="11" y2="14" />
                                <line x1="8" y1="11" x2="14" y2="11" />
                            </svg>
                        </button>
                    </div>

                    <div className="imgslider-nav">
                        <button
                            className="imgslider-arrow imgslider-prev"
                            type="button"
                            aria-label={t('site.programs.previous')}
                            onClick={() => go(current - 1)}
                        >
                            <svg viewBox="0 0 24 24">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>

                        <div className="imgslider-dots">
                            {programSlides.map((slide, position) => (
                                <button
                                    key={slide}
                                    type="button"
                                    className={
                                        position === current
                                            ? 'imgslider-dot imgslider-dot-active'
                                            : 'imgslider-dot'
                                    }
                                    aria-label={`${t('site.hero.slide')} ${String(position + 1)}`}
                                    onClick={() => go(position)}
                                />
                            ))}
                        </div>

                        <button
                            className="imgslider-arrow imgslider-next"
                            type="button"
                            aria-label={t('site.programs.next')}
                            onClick={() => go(current + 1)}
                        >
                            <svg viewBox="0 0 24 24">
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </button>
                    </div>

                    <a
                        href={company.whatsApp}
                        className="imgslider-cta"
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        <img src="/media/icons/whatsapp.svg" alt="WhatsApp" className="imgslider-cta-icon" />
                        <span>{t('site.hero.cta')}</span>
                    </a>
                </div>
            </div>

            {openedAt !== null && (
                <ProgramLightbox
                    images={programSlides}
                    startIndex={openedAt}
                    onClose={() => setOpenedAt(null)}
                />
            )}
        </section>
    );
}
